import type { InputHTMLAttributes, ReactNode } from 'react';

export interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  children?: ReactNode;
}

export function FormField({ label, error, children, id, ...inputProps }: FormFieldProps) {
  const fieldId = id ?? `field-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="mb-4">
      <label
        htmlFor={fieldId}
        className="block mb-1.5 text-sm font-medium text-text-secondary"
      >
        {label}
      </label>

      {children ?? (
        <input
          id={fieldId}
          {...inputProps}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          className={`w-full py-2 px-3 rounded-lg text-sm border bg-surface text-text-primary ${
            error
              ? 'border-red-400 focus:ring-red-300/50 focus:border-red-400'
              : 'border-border focus:ring-baker-accent/40 focus:border-baker-accent'
          } focus:outline-none focus:ring-2 transition-all placeholder:text-text-muted`}
        />
      )}

      {error && (
        <p
          id={`${fieldId}-error`}
          role="alert"
          className="mt-1.5 text-xs text-red-600 flex items-center gap-1"
        >
          <svg className="h-3.5 w-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}
